import React, { useEffect, useMemo, useState } from "react"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, FilterSegment, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

// Отчёт «Конструктор и комбо»: что собирают гости, какие компоненты берут чаще,
// сколько отдано бесплатно и сколько заработали на доплатах (кабинет только по-русски)

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "14px",
	boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
}

const mutedStyle = { color: "var(--dark-text-muted, #8791a1)", fontSize: "12px" }

const cellStyle = { borderBottom: "1px solid var(--dark-border, #eef2f6)" }

const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }

const TYPE_META = {
	combo: { label: "Комбо", color: "#7c3aed", bg: "var(--dark-tint-info, #f1eafe)" },
	groups: { label: "Конструктор", color: "#2563eb", bg: "var(--dark-tint-primary, #e8f0fe)" },
	halves: { label: "Половинки", color: "#e0762c", bg: "var(--dark-tint-warning, #fff4df)" },
}

const TYPE_OPTIONS = [
	{ id: "all", label: "Все" },
	{ id: "combo", label: "Комбо" },
	{ id: "groups", label: "Конструктор" },
]

const VIEW_OPTIONS = [
	{ id: "dishes", label: "Блюда", icon: "uil-layer-group" },
	{ id: "components", label: "Компоненты", icon: "uil-apps" },
	{ id: "slots", label: "Слоты", icon: "uil-chart-bar" },
]

function KpiCard({ title, value, sublabel, icon, color = "#2563eb" }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body p-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.4px" }}>{title}</div>
					<div className="d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", borderRadius: "11px", background: `${color}18`, color }}>
						<i className={`uil ${icon}`}></i>
					</div>
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "26px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>{value}</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function TypeBadge({ type }) {
	const meta = TYPE_META[type] || TYPE_META.groups
	return (
		<span style={{ background: meta.bg, color: meta.color, borderRadius: "12px", padding: "2px 8px", fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap" }}>
			{meta.label}
		</span>
	)
}

// Что получил гость без доплаты: у комбо это «входит в комбо», у конструктора — «бесплатно»
function freeText(units, value) {
	if (!(Number(units) > 0)) return <span className="text-muted">—</span>
	return (
		<>
			<span style={{ fontWeight: 700 }}>{formatMoney(units, 0)} шт</span>
			<div style={mutedStyle}>на {formatMoney(value, 0)} по прайсу</div>
		</>
	)
}

export function DishesTable({ rows, totals }) {
	const totalRevenue = Number(totals.revenue || 0)
	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Блюдо</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Порций</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Цена порции</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Доплаты</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Бесплатно</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Входит в комбо</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Выручка</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Доля</th>
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 && <tr><td colSpan="8" className="text-muted text-center py-4">Продаж конструкторов и комбо за период нет</td></tr>}
							{rows.map(row => {
								const portions = Number(row.portions || 0)
								const revenue = Number(row.revenue || 0)
								const extras = Number(row.extras_revenue || 0)
								return (
									<tr key={row.id}>
										<td className="py-3" style={cellStyle}>
											<div className="d-flex align-items-center gap-2 flex-wrap">
												<span style={{ fontWeight: 700 }}>{row.name}</span>
												<TypeBadge type={row.type} />
											</div>
										</td>
										<td className="py-3 text-end" style={cellStyle}>{formatMoney(portions, 0)}</td>
										<td className="py-3 text-end" style={cellStyle}>{portions > 0 ? formatMoney(revenue / portions, 0) : "—"}</td>
										<td className="py-3 text-end" style={cellStyle}>
											{extras > 0 ? formatMoney(extras, 0) : <span className="text-muted">—</span>}
											{extras > 0 && portions > 0 && <div style={mutedStyle}>{formatMoney(extras / portions, 0)} на порцию</div>}
										</td>
										<td className="py-3 text-end" style={cellStyle}>{freeText(row.free_units, row.free_value)}</td>
										<td className="py-3 text-end" style={cellStyle}>{freeText(row.combo_units, row.combo_value)}</td>
										<td className="py-3 text-end" style={{ ...cellStyle, fontWeight: 800 }}>{formatMoney(revenue, 0)}</td>
										<td className="py-3 text-end text-muted" style={cellStyle}>{totalRevenue > 0 ? formatMoney(revenue * 100 / totalRevenue, 1) : 0}%</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

export function ComponentsTable({ rows, dishes }) {
	const dishById = useMemo(() => Object.fromEntries(dishes.map(dish => [Number(dish.id), dish])), [dishes])
	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="mb-2" style={mutedStyle}>
					У комбо выручка блюда (цена набора и доплаты) распределена между компонентами по прайсу — так компоненты не выглядят непроданными.
					У конструктора выручка компонента — то, что за него реально заплатили.
				</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Компонент</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Блюдо / слот</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Выбрано</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>В порциях</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Доплаты</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Бесплатно / в комбо</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Выручка</th>
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 && <tr><td colSpan="7" className="text-muted text-center py-4">Компонентов за период нет</td></tr>}
							{rows.map(row => {
								const dish = dishById[Number(row.dish_product_id)]
								const dishPortions = Number(dish?.portions || 0)
								const withShare = dishPortions > 0 ? Number(row.portions_with || 0) * 100 / dishPortions : 0
								const isCombo = row.type === "combo"
								return (
									<tr key={`${row.dish_product_id}:${row.id}`}>
										<td className="py-3" style={{ ...cellStyle, fontWeight: 700 }}>{row.name}</td>
										<td className="py-3" style={cellStyle}>
											<div>{dish?.name || "—"}</div>
											<div style={mutedStyle}>{row.group_name || "слот не найден (группа снята с блюда)"}</div>
										</td>
										<td className="py-3 text-end" style={cellStyle}>{formatMoney(row.units, 0)}</td>
										<td className="py-3 text-end text-muted" style={cellStyle}>{formatMoney(withShare, 1)}%</td>
										<td className="py-3 text-end" style={cellStyle}>
											{Number(row.paid_revenue) > 0 ? <>{formatMoney(row.paid_revenue, 0)}<div style={mutedStyle}>{formatMoney(row.paid_units, 0)} шт</div></> : <span className="text-muted">—</span>}
										</td>
										<td className="py-3 text-end" style={cellStyle}>
											{isCombo
												? (Number(row.combo_units) > 0 ? <span style={{ fontWeight: 700 }}>{formatMoney(row.combo_units, 0)} шт в комбо</span> : <span className="text-muted">—</span>)
												: freeText(row.free_units, row.free_value)}
										</td>
										<td className="py-3 text-end" style={{ ...cellStyle, fontWeight: 800 }}>{formatMoney(row.allocated_revenue, 0)}</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

export function SlotsList({ slots }) {
	if (slots.length === 0) {
		return <div className="card" style={cardStyle}><div className="card-body text-muted text-center py-4">Выборов в слотах за период нет</div></div>
	}
	return (
		<div className="row g-3">
			{slots.map(slot => (
				<div className="col-xl-4 col-md-6" key={`${slot.dish_id}:${slot.group_id ?? 0}`}>
					<div className="card h-100" style={cardStyle}>
						<div className="card-body">
							<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.4px" }}>{slot.dish_name}</div>
							<div style={{ fontWeight: 800, fontSize: "16px" }}>{slot.name || "Без слота"}</div>
							<div className="mb-3" style={mutedStyle}>{formatMoney(slot.units, 0)} выборов</div>
							{slot.variants.map(variant => (
								<div className="mb-2" key={variant.id}>
									<div className="d-flex justify-content-between" style={{ fontSize: "13px" }}>
										<span style={{ fontWeight: 600 }}>{variant.name}</span>
										<span>
											{formatMoney(variant.units, 0)} · {formatMoney(variant.share, 1)}%
											{Number(variant.paid_revenue) > 0 && <span style={{ color: "#16a34a", fontWeight: 700 }}> · +{formatMoney(variant.paid_revenue, 0)}</span>}
										</span>
									</div>
									<div style={{ height: "6px", borderRadius: "3px", background: "var(--dark-border, #eef2f6)", marginTop: "3px" }}>
										<div style={{ width: `${Math.min(100, variant.share)}%`, height: "100%", borderRadius: "3px", background: "#2563eb" }}></div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			))}
		</div>
	)
}

function Index() {
	const [data, setData] = useState(null)
	const [dishList, setDishList] = useState([])
	const [view, setView] = useState("dishes")

	const filters = useAnalyticsFilters({
		...buildPeriod("30days"),
		// Конструкторы смотрят по сети: на одном филиале продаж может не быть
		pos_id: -1,
		order_type_ids: [],
		source_ids: [],
		dish_ids: [],
		types: "all",
	}, searchWithFilter)
	const { draft, patch } = filters
	const options = useAnalyticsOptions(["sources"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/constructor", filter, { types: filter.types === "all" ? "" : filter.types }), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	async function getDishes() {
		const response = await GET("/analytics/constructor-dishes")
		if (httpOk(response)) setDishList(response.data)
	}

	useEffect(() => {
		getDishes()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// В селекте блюд — только блюда выбранного типа
	const dishOptions = useMemo(() => dishList
		.filter(dish => draft.types === "all" || dish.type === draft.types)
		.map(dish => ({ ...dish, label: `${dish.name} · ${(TYPE_META[dish.type] || TYPE_META.groups).label}` })), [dishList, draft.types])

	const totals = data?.totals || {}
	const dishes = data?.dishes || []
	const components = data?.components || []
	const slots = data?.slots || []

	return (
		<>
			<AnalyticsFilterBar
				title="Конструктор и комбо"
				icon="uil-layer-group"
				subtitle="Что собирают гости: популярные компоненты, доплаты и бесплатные штуки"
				filters={filters}
				presets={["yesterday", "7days", "30days", "this_month", "last_month", "custom"]}
			>
				<StandardFields filters={filters} options={options} />
				<FilterField label="Тип блюда" wide>
					<FilterSegment options={TYPE_OPTIONS} value={draft.types} onChange={(id) => patch({ types: id, dish_ids: [] })} />
				</FilterField>
				<FilterField label="Блюдо" wide>
					<FilterSelect isMulti options={dishOptions} allLabel="Все блюда" getOptionLabel={(option) => option.label} value={draft.dish_ids} onChange={(ids) => patch({ dish_ids: ids })} />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<KpiCard
						title="Порций"
						value={formatMoney(totals.portions || 0, 0)}
						sublabel={`${formatMoney(totals.revenue_share || 0, 1)}% выручки периода · ${formatMoney(totals.avg_units || 0, 1)} компонента в порции`}
						icon="uil-layer-group"
						color="#2563eb"
					/>
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard
						title="Выручка"
						value={formatMoney(totals.revenue || 0, 0)}
						sublabel={`Средняя порция ${formatMoney(totals.avg_portion_price || 0, 0)}`}
						icon="uil-money-bill"
						color="#16a34a"
					/>
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard
						title="Доплаты"
						value={formatMoney(totals.extras_revenue || 0, 0)}
						sublabel={`${formatMoney(totals.avg_extras || 0, 0)} на порцию сверх цены блюда`}
						icon="uil-plus-circle"
						color="#e0762c"
					/>
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard
						title="Отдано бесплатно"
						value={formatMoney(totals.free_value || 0, 0)}
						sublabel={`${formatMoney(totals.free_units || 0, 0)} шт по прайсу · в комбо без доплаты ${formatMoney(totals.combo_units || 0, 0)} шт`}
						icon="uil-gift"
						color="#dc2626"
					/>
				</div>
			</div>

			<div className="mb-3">
				<FilterSegment options={VIEW_OPTIONS} value={view} onChange={setView} />
			</div>

			{view === "dishes" && <DishesTable rows={dishes} totals={totals} />}
			{view === "components" && <ComponentsTable rows={components} dishes={dishes} />}
			{view === "slots" && <SlotsList slots={slots} />}
		</>
	)
}

export default Index
